// Locks the web session after a stretch with no clicks, keys or scrolling.
//
// The phone locks on the way back to the foreground after the app has been
// in the background longer than the auto-lock threshold (App.tsx, via
// AppState). react-native-web maps AppState onto tab visibility, so on the
// web that half already works: a tab hidden behind another for longer than
// the threshold locks when it is shown again. What it never covered is the
// case the Auto-Lock Timer screen actually describes: a tab left open and
// visible on a shared computer with nobody touching it. This installs that
// half. Both paths call the same `lockNow`, and the overlay
// (src/components/IdleLockOverlay.web.tsx) handles the unlock.
//
// Activity is a click, a key, a wheel tick, a touch or a scroll. Moving the
// mouse is deliberately not activity: a nudged desk should not keep a vault
// open.

import { isLocked, lockNow, subscribeToLock } from '../../../src/services/auth/idleLock';
import { getIdleLockThreshold } from '../../../src/services/auth/idleLockPreferences';
import { getAuthToken } from '../../../src/services/auth/tokenStorage';

export const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;
export const POLL_MS = 5_000;

export interface InactivityLockDeps {
  now?: () => number;
  getThreshold?: () => Promise<number>;
  hasSession?: () => Promise<boolean>;
  locked?: () => boolean;
  lock?: () => void;
  onLockChange?: (fn: (locked: boolean) => void) => () => void;
  target?: EventTarget;
  pollMs?: number;
}

/** Starts the timer; returns a teardown. Safe to call once per page. */
export function installInactivityLock(deps: InactivityLockDeps = {}): () => void {
  const now = deps.now ?? Date.now;
  const getThreshold = deps.getThreshold ?? getIdleLockThreshold;
  const hasSession = deps.hasSession ?? (async () => !!(await getAuthToken()));
  const locked = deps.locked ?? isLocked;
  const lock = deps.lock ?? lockNow;
  const onLockChange = deps.onLockChange ?? subscribeToLock;
  const target = deps.target ?? (typeof window !== 'undefined' ? window : undefined);
  const pollMs = deps.pollMs ?? POLL_MS;

  let lastActivity = now();
  const touch = () => {
    lastActivity = now();
  };
  ACTIVITY_EVENTS.forEach((e) => target?.addEventListener(e, touch, { passive: true, capture: true }));

  // An unlock (password, or Sign Out from the overlay) restarts the clock.
  const offLock = onLockChange((isNowLocked) => {
    if (!isNowLocked) lastActivity = now();
  });

  let busy = false;
  const tick = async () => {
    if (busy || locked()) return;
    busy = true;
    try {
      if (now() - lastActivity < (await getThreshold())) return;
      if (!(await hasSession())) {
        // Signed out: nothing to protect. Restart so a later sign-in gets a
        // full threshold before its first lock.
        lastActivity = now();
        return;
      }
      lock();
    } catch {
      // A failed read of the threshold or token must never keep a vault
      // open forever or lock it spuriously; the next tick tries again.
    } finally {
      busy = false;
    }
  };
  const timer = setInterval(() => void tick(), pollMs);

  return () => {
    clearInterval(timer);
    offLock();
    ACTIVITY_EVENTS.forEach((e) => target?.removeEventListener(e, touch, { capture: true }));
  };
}
